// src/hooks/useIconReplacementState.ts
/**
 * Hook to determine if an icon should be hidden during replacement hover
 *
 * Supports both:
 * 1. PLACE_ICON mode - When placing new icon from sidebar over existing
 * 2. DRAG_ITEMS mode - When dragging existing icon over another
 *
 * Usage in Node component:
 * const shouldHide = useIconReplacementState(node.id);
 *
 * Then apply opacity: shouldHide ? 0 : 1 to the node
 */
import { useMemo } from 'react';
import { useUiStateStore } from '@/stores/uiStateStore';
import { useScene } from '@/hooks/useScene';
import { getItemAtTile } from '@/utils';

export const useIconReplacementState = (itemId: string): boolean => {
    const mode = useUiStateStore((state) => state.mode);
    const mouse = useUiStateStore((state) => state.mouse);
    const scene = useScene();

    // Check if this specific item is being hovered for replacement
    const shouldHide = useMemo(() => {
        // Mode 1: PLACE_ICON - placing new icon from sidebar
        if (mode.type === 'PLACE_ICON' && mode.id) {
            // Check if hovering over this specific item
            const itemAtTile = getItemAtTile({
                tile: mouse.position.tile,
                scene
            });

            // Hide this item if it's the one being hovered for replacement
            return itemAtTile?.type === 'ITEM' && itemAtTile.id === itemId;
        }

        // Mode 2: DRAG_ITEMS - dragging existing icon over another
        if (mode.type === 'DRAG_ITEMS' && mode.items?.length === 1 && mode.items[0].type === 'ITEM') {
            const draggedItemId = mode.items[0].id;

            // Don't hide the item being dragged
            if (itemId === draggedItemId) {
                return false;
            }

            // Check if hovering over this specific item
            const itemAtTile = getItemAtTile({
                tile: mouse.position.tile,
                scene
            });

            // Hide this item if it's being hovered over (target for replacement)
            return itemAtTile?.type === 'ITEM' && itemAtTile.id === itemId;
        }

        return false;
    }, [mode, mouse.position.tile, scene, itemId]);

    return shouldHide;
};
